import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Box, Button, Checkbox, Paper, Typography, IconButton } from "@mui/material";
import { Delete, History, Info } from "@mui/icons-material";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import TablePaginationComponent from "../table/table-pagination.component";
import DialogVersion from "../dialog/dialog.component";
import { RFCExecution } from "../../models/general/import";
import HistoryComponent from "../history/history.component";
import { PaginatedResult } from "../../models/default/paginated-result";
import { BaseService } from "../../services/base-service";
import { URLS } from "../../services/app-urls";
import { ErrorComponent } from "../error/error.component";
import { translate } from "../../translate/translate";
import Loading from "../loading/loading";
import RFCDialog from "../dialog/rfc-dialog.component";

const RFCExecutionHistoryComponent = ({
  displayedColumnsShow,
  displayedColumns,
  onlyInfoAction,
  rfcType
}: {
  displayedColumnsShow?: string[];
  displayedColumns?: string[];
  onlyInfoAction?: boolean;
  rfcType?: string;
}) => {
  const service = new BaseService<RFCExecution>(URLS.RFC_EXECUTIONS);
  const [rfcExecution, setRfcExecution] = useState<RFCExecution>();
  const [dataSource, setDataSource] = useState<RFCExecution[] | any>([]);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<RFCExecution | null>(null);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openHistory, setOpenHistory] = useState<boolean>(false);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [idHistory, setIdHistory] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);


  const autoRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoRefreshInFlightRef = useRef(false);
  const isMountedRef = useRef<boolean>(true);

  const getStatusLabel = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      "PROCESSING": translate.t("processing_import"),
      "UPDATED": translate.t("updated"),
      "PARTIALLY_UPDATED": translate.t("partially_updated"),
      "ERROR": translate.t("error"),
    };
    return statusMap[status] || status;
  };

  const hasProcessingRFCs = useCallback((): boolean => {
    return dataSource.some((item: RFCExecution) => item.status === "PROCESSING");
  }, [dataSource]);


  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshTimeoutRef.current) {
      clearTimeout(autoRefreshTimeoutRef.current);
      autoRefreshTimeoutRef.current = null;
    }
  }, []);

  const startAutoRefresh = useCallback(async () => {
    stopAutoRefresh();

    if (!hasProcessingRFCs()) {
      return;
    }

    const runAutoRefresh = async () => {
      if (!isMountedRef.current) return;
      if (autoRefreshInFlightRef.current) return;

      autoRefreshInFlightRef.current = true;
      let shouldScheduleNext = true;

      try {
        const serviceTemp = new BaseService<RFCExecution>(URLS.RFC_EXECUTIONS);
        serviceTemp.clearParameters();
        serviceTemp.addParameter("limit", rowsPerPage);
        serviceTemp.addParameter("offset", page * rowsPerPage);

        if (rfcType) {
          serviceTemp.addParameter("rfc_type", rfcType);
        }

        const response = await serviceTemp.getAllPaginate();

        if (!isMountedRef.current) return;

        setPagination(response.data);

        const formattedData = response.data.results.map((item: RFCExecution) => {
          const model = Object.assign(new RFCExecution(), item);
          return {
            ...model,
            import_date: model.started_at || model.created_at || model.execution_date,
            user_name: model.initiated_by_label || "System",
            is_success: model.status === "UPDATED" || model.status === "PARTIALLY_UPDATED",
            status_raw: model.status,
            status_message: getStatusLabel(model.status || ""),
          };
        });

        setDataSource(formattedData);

        const hasProcessing = formattedData.some((item: RFCExecution) => item.status === "PROCESSING");
        if (!hasProcessing) {
          shouldScheduleNext = false;
          stopAutoRefresh();
          return;
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error("Erro ao fazer auto-refresh de RFC:", error);
        }
      } finally {
        autoRefreshInFlightRef.current = false;

        if (isMountedRef.current && shouldScheduleNext) {
          autoRefreshTimeoutRef.current = setTimeout(runAutoRefresh, 5000);
        }
      }
    };

    autoRefreshTimeoutRef.current = setTimeout(runAutoRefresh, 5000);
  }, [hasProcessingRFCs, rowsPerPage, page, rfcType, stopAutoRefresh]);

  const RFC_TYPE_LABEL: Record<string, string> = {
    BOM: translate.t("bom"),
    FORECAST: translate.t("forecast"),
    STOCK_PO: translate.t("stock_and_po"),
  };

  const OPERATION_LABEL: Record<string, string> = {
    AUTOMATIC: translate.t("automatic"),
    MANUAL: translate.t("manual"),
  };

  const search = async (restartIndex = false) => {
    try {
      setIsLoading(true);
      const service = new BaseService<RFCExecution>(URLS.RFC_EXECUTIONS);
      service.clearParameters();
      service.addParameter("limit", rowsPerPage);
      service.addParameter("offset", page * rowsPerPage);

      if (rfcType) {
        service.addParameter("rfc_type", rfcType);
      }

      const response = await service.getAllPaginate();

      setPagination(response.data);

      const formattedData = response.data.results.map((item: RFCExecution) => {
        const model = Object.assign(new RFCExecution(), item);
        return {
          ...model,
          import_date: model.started_at || model.created_at || model.execution_date,
          user_name: model.initiated_by_label || "System",
          is_success: model.status === "UPDATED" || model.status === "PARTIALLY_UPDATED",
          status_raw: model.status,
          status_message: getStatusLabel(model.status || ""),
        };
      });

      setDataSource(formattedData);

      if (restartIndex) setPage(0);
    } catch (error) {
      ErrorComponent(error as AxiosError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    search();
  }, [page, rowsPerPage, rfcType]);

  useEffect(() => {
    startAutoRefresh();

    return () => {
      stopAutoRefresh();
    };
  }, [dataSource, startAutoRefresh, stopAutoRefresh]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopAutoRefresh();
    };
  }, [stopAutoRefresh]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderStatusCell = (message: string, status: string) => {
    let textColor;

    switch (status) {
      case "UPDATED":
        textColor = "#4CAF4F";
        break;
      case "PARTIALLY_UPDATED":
        textColor = "#FF9800";
        break;
      case "ERROR":
        textColor = "#F44336";
        break;
      case "PROCESSING":
        textColor = "#3639f4ff";
        break;
      default:
        textColor = "#212121";
    }

    return (
      <Box display="flex" alignItems="center" textAlign={"left"} maxWidth={{ xs: "200px", lg: "300px", xl: "unset" }}>
        <Typography sx={{ color: textColor }}>{message}</Typography>
      </Box>
    );
  };

  const handleOpenDeleteModal = (row: RFCExecution) => {
    setRfcExecution(row);
    setOpenDeleteModal(true);
  }

  const toggleSelectAll = useCallback(() => {
    const selectableIds = dataSource
      .filter((item: RFCExecution) => item.status !== "PROCESSING")
      .map((item: RFCExecution) => item.id);

    if (selectedIds.length === selectableIds.length && selectedIds.length > 0) {
      setSelectedIds([]);
    } else {

      setSelectedIds(selectableIds);
    }
  }, [selectedIds, dataSource]);

  const toggleSelectOne = useCallback((id: number) => {
    const item = dataSource.find((i: RFCExecution) => i.id === id);

    if (item?.status === "PROCESSING") {
      toast.warning(translate.t("cannot_delete_processing_rfc") || "Não é possível deletar RFC em processo");
      return;
    }

    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, [dataSource]);

  const renderCheckboxCell = useCallback(
    (row: RFCExecution) => {
      const isProcessing = row.status === "PROCESSING";
      return (
        <Box display="flex" alignItems="left" justifyContent="left" sx={{ height: "100%" }}>
          <Checkbox
            checked={selectedIds.includes(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelectOne(row.id)}
            disabled={isProcessing}
            color="primary"
            sx={{
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          />
        </Box>
      );
    },
    [selectedIds, toggleSelectOne]
  );

  const renderHeaderCheckbox = useCallback(
    () => {

      const selectableItems = dataSource.filter((item: RFCExecution) => item.status !== "PROCESSING");
      const selectableIds = selectableItems.map((item: RFCExecution) => item.id);

      const allSelectableSelected = selectableIds.length > 0 &&
        selectableIds.every((id: number) => selectedIds.includes(id));

      const someSelected = selectedIds.length > 0 && !allSelectableSelected;

      return (
        <Box display="flex" alignItems="left" justifyContent="left" sx={{ height: "100%" }}>
          <Checkbox
            indeterminate={someSelected}
            checked={allSelectableSelected}
            onChange={toggleSelectAll}
            sx={{
              "& .MuiSvgIcon-root": { fontSize: 20 },
              color: "white",
              "& .MuiSvgIcon-root path": {
                stroke: "white",
              },
              "&.Mui-checked": {
                color: "white",
              },
              "&.MuiCheckbox-indeterminate": {
                color: "white",
              },
              "& .MuiTouchRipple-root": {
                color: "white",
              },
            }}
          />
        </Box>
      );
    },
    [selectedIds, dataSource, toggleSelectAll]
  );

  const renderCellHandlerConfig = useMemo(() => ({
    select: (_: string, __: number, row: any) => renderCheckboxCell(row),
    status_message: (_: string, __: number, row: any) => renderStatusCell(row["status_message"], row["status_raw"]),
    operation: (_: string, __: number, row: any) => {
      const operationText = row["operation"] === "AUTOMATIC" ? translate.t("automatic") : translate.t("manual");
      return <Typography>{operationText}</Typography>;
    },
    action: (_: string, __: number, row: any) => {
      if (!onlyInfoAction) return null;
      return (
        <IconButton
          sx={{ color: "#546E7A" }}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedAnalysis(row);
            setOpenInfoDialog(true);
          }}
        >
          <Info />
        </IconButton>
      );
    },
  }), [renderCheckboxCell, onlyInfoAction]);

  const confirmDeleteMessage = useMemo(() => {
    if (selectedIds.length > 1) {
      return translate.t("confirm_delete_itens");
    }
    return translate.t("confirm_delete");
  }, [selectedIds]);

  const handleCloseDeleteModal = useCallback(
    async (result: unknown) => {
      if (typeof result === "boolean" && result) {
        const idsToDelete =
          selectedIds.length > 0 ? selectedIds : rfcExecution ? [rfcExecution.id] : [];

        if (idsToDelete.length === 0) {
          setOpenDeleteModal(false);
          return;
        }

        try {
          setIsLoading(true);
          await service.postFromListRoute({ "ids": idsToDelete, }, "bulk-delete");

          toast.success(
            idsToDelete.length > 1
              ? translate.t("all_successfully_deleted")
              : translate.t("successfully_deleted")
          );

          search();
        } catch (error: any) {
          ErrorComponent(error as any);
        } finally {
          setIsLoading(false);
          setSelectedIds([]);
          setRfcExecution(undefined);
          setOpenDeleteModal(false);
        }
      } else {
        setRfcExecution(undefined);
        setOpenDeleteModal(false);
      }
    },
    [selectedIds, rfcExecution, service, search]
  );

  const renderSelectedRFCExecutions = useCallback(() => {
    const executions = dataSource.filter((i: any) => selectedIds.includes(i.id));
    if (executions.length === 0) return null;

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: "10px",
          border: "1px solid #E0E0E0",
          backgroundColor: "#FAFAFA",
          padding: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: 300,
          overflowY: "overlay",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {executions.map((item: any, idx: number) => {
          return (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: "40px",
              }}
            >
              {/* Descrição */}
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  color: "#212121",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingLeft: "12px",
                }}
              >
                {`${RFC_TYPE_LABEL[item?.rfc_type] ?? item?.rfc_type} - ${
  item?.execution_date
} (${OPERATION_LABEL[item?.operation] ?? item?.operation})`}
              </Typography>
            </Paper>
          );
        })}
      </Paper>
    );
  }, [dataSource, selectedIds]);

  return (
    <Box p={3}>
      <Loading open={isLoading} />
      <Box display="flex" alignItems="center" gap={1}>
        <History sx={{ color: "#546E7A" }} />
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "24.2px",
            textAlign: "left",
          }}
        >
          {translate.t("import_history")}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center">
        {selectedIds.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#FFF8E1",
              border: "1px solid #F8B653",
              borderRadius: "8px",
              padding: "12px 16px",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ color: "#FBC02D" }} />
              <Typography sx={{ color: "#6D4C41", fontWeight: 500 }}>
                {selectedIds.length}{" "}
                {selectedIds.length === 1
                  ? translate.t("selected_record")
                  : translate.t("selected_records")}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<Delete />}
              sx={{
                backgroundColor: "#3949AB",
                "&:hover": { backgroundColor: "#1F0063" },
                textTransform: "none",
                fontWeight: 600,
              }}
              onClick={() => {
                selectedIds.forEach((id) => {
                  const selected = dataSource.find((item: RFCExecution | any) => item.id === id);
                  if (selected) handleOpenDeleteModal(selected);
                });
              }}
            >
              {[translate.t("delete_item")]}
            </Button>
          </Box>
        )}
      </Box>

      <Box mt={3}>
        <TablePaginationComponent
          state={dataSource}
          displayedColumnsShow={displayedColumnsShow ?? ["select", "import_date", "status", "operation"]}
          displayedColumns={displayedColumns ?? ["select", "import_date", "status_message", "operation"]}
          dateColumns={["import_date"]}
          count={pagination?.count}
          next={pagination?.next}
          previous={pagination?.previous}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          renderCellHandler={renderCellHandlerConfig}
          renderHeaderCheckbox={renderHeaderCheckbox}
          handleOpenDialogHistory={(id: number) => { setIdHistory(id); setOpenHistory(true); }}
        />
      </Box>

      {openDeleteModal && (
        <DialogVersion
          id="dialog-delete-rfc-execution"
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          title={translate.t("delete_import_history")}
          message={confirmDeleteMessage}
          labelButton={translate.t("delete")}
          colorButton="error"
        >
          {renderSelectedRFCExecutions()}
        </DialogVersion>
      )}
      {/* Histórico detalhado (dialog) */}
      {openHistory && (
        <HistoryComponent
          openHistory={openHistory}
          handleCloseDialogHistory={() => setOpenHistory(false)}
          idHistory={idHistory}
          columns_history={["rfc_type", "status_message", "initiated_by_label", "execution_date", "execution_log"]}
          service={URLS.RFC_EXECUTIONS}
          dateColumns={["execution_date"]}
        />
      )}
       {openInfoDialog && (
        <RFCDialog
          open={openInfoDialog}
          rfcsap={selectedAnalysis}
          onClose={() => {
            setOpenInfoDialog(false);
            setSelectedAnalysis(null);
          }}
        />
      )}
    </Box>
  );
};

export default RFCExecutionHistoryComponent;
